import { useMemo } from 'react';
import { AnomalyDetectionService } from '@/services/anomalyDetectionService';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import { getViolationsForEmployee } from '@/data/mockPolicyViolations';
import { calculateViolationsForTimesheets } from '@/services/policyViolationService';
import type { Employee, Timesheet } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

interface UseApprovalAnomaliesParams {
  pendingTimesheets: Timesheet[];
  employees: Employee[];
  timesheets: Timesheet[];
  orgSettings: OrganisationTimesheetSettings;
  screenMode: 'demo' | 'live';
  settingsLoading: boolean;
  settingsError: Error | null;
}

export interface UseApprovalAnomaliesReturn {
  timesheetAnomalies: Map<number, AnomalyDetectionResult>;
  timesheetPolicyViolations: Map<number, PolicyViolation[]>;
}

/**
 * Derives detected anomalies and policy violations for every pending timesheet.
 */
export function useApprovalAnomalies({
  pendingTimesheets,
  employees,
  timesheets,
  orgSettings,
  screenMode,
  settingsLoading,
  settingsError,
}: UseApprovalAnomaliesParams): UseApprovalAnomaliesReturn {
  // Calculate anomalies for all timesheets
  // Pass organization settings to use settings-based limits instead of hardcoded defaults
  const timesheetAnomalies = useMemo(() => {
    const anomalyMap = new Map<number, AnomalyDetectionResult>();

    pendingTimesheets.forEach(timesheet => {
      const employee = employees.find(e => e.id === timesheet.employeeId);
      if (!employee) return;

      // Calculate historical data for the employee
      const historicalData = AnomalyDetectionService.calculateHistoricalData(
        timesheet.employeeId,
        timesheets
      );

      // Detect anomalies - pass orgSettings to use settings-based limits
      const anomalyResult = AnomalyDetectionService.detectAnomalies(
        timesheet,
        employee,
        historicalData,
        timesheets,
        orgSettings
      );

      anomalyMap.set(timesheet.id, anomalyResult);
    });

    return anomalyMap;
  }, [pendingTimesheets, employees, timesheets, orgSettings]);

  // Calculate policy violations for all timesheets
  // In live mode: use backend settings to calculate violations
  // In demo mode: use mock data
  const timesheetPolicyViolations = useMemo(() => {
    const violationsMap = new Map<number, PolicyViolation[]>();

    if (screenMode === 'live') {
      // Live mode: calculate violations from backend settings
      // Also skip if settings fetch failed — avoids using default settings
      // that would re-enable all disabled policy checks
      if (settingsLoading || !orgSettings || settingsError) {
        return violationsMap;
      }
      return calculateViolationsForTimesheets(pendingTimesheets, orgSettings);
    } else {
      // Demo mode: use mock data
      pendingTimesheets.forEach(ts => {
        const violations = getViolationsForEmployee(ts.employeeId);
        if (violations.length > 0) {
          violationsMap.set(ts.id, violations);
        }
      });
      return violationsMap;
    }
  }, [pendingTimesheets, orgSettings, settingsLoading, settingsError, screenMode]);

  return { timesheetAnomalies, timesheetPolicyViolations };
}
